/**
 * Que faire quand une question n'a pas abouti : peut-on la renvoyer, et quand ?
 *
 * Critère du ticket W3-2 : « les erreurs réseau permettent une nouvelle
 * tentative ». Avant, le widget affichait un message d'erreur après avoir déjà
 * vidé le champ de saisie : le visiteur devait retaper sa question.
 *
 * Aucun accès au DOM ni au réseau : c'est ce qui rend ces décisions testables.
 */

export type ChatErrorKind = 'quota' | 'origin' | 'session' | 'rate' | 'offline' | 'server';

/** Ce que dit un statut HTTP de refus du `/runtime/chat`. */
export function classerStatut(status: number): ChatErrorKind {
  switch (status) {
    case 402:
      return 'quota';
    case 403:
      return 'origin';
    // « Session introuvable » : purgée par la rétention, ou jamais ouverte. Un
    // nouvel essai doit en rouvrir une, sinon il échouerait à l'infini.
    case 404:
      return 'session';
    case 429:
      return 'rate';
    default:
      return 'server';
  }
}

/**
 * Un nouvel essai a-t-il une chance d'aboutir ?
 *
 * Non pour un quota épuisé ou un domaine non autorisé : rien ne changera d'ici
 * le prochain clic, et proposer le bouton serait promettre ce qu'on ne tiendra
 * pas.
 */
export function peutReessayer(kind: ChatErrorKind): boolean {
  return kind !== 'quota' && kind !== 'origin';
}

const ATTENTE_RATE_DEFAUT_MS = 10_000;
const ATTENTE_RATE_MAX_MS = 60_000;

/**
 * Combien attendre avant que le bouton soit utilisable.
 *
 * Seule la limite de débit impose une attente : cliquer tout de suite rendrait
 * un nouveau 429. L'API donne `Retry-After` en secondes ; bornée à une minute,
 * pour qu'une valeur aberrante ne rende pas le bouton inutilisable.
 */
export function delaiAvantReessai(kind: ChatErrorKind, retryAfter: string | null | undefined): number {
  if (kind !== 'rate') return 0;
  const secondes = retryAfter ? Number(retryAfter) : Number.NaN;
  if (!Number.isFinite(secondes) || secondes <= 0) return ATTENTE_RATE_DEFAUT_MS;
  return Math.min(ATTENTE_RATE_MAX_MS, Math.max(1_000, Math.ceil(secondes) * 1_000));
}

/**
 * Libellé du bouton. Posé une fois à l'affichage et une fois à l'échéance, pas
 * à chaque seconde : la zone des messages est annoncée par les lecteurs d'écran,
 * et un compte à rebours y serait lu seconde par seconde.
 */
export function libelleReessai(resteMs: number): string {
  return resteMs > 0 ? `Réessayer dans ${Math.ceil(resteMs / 1_000)} s` : 'Réessayer';
}
